import { createRouter, createWebHistory, type RouteRecordRaw } from 'vue-router';

import { useAuthStore } from '@/stores/auth';
import { healStaleBuild } from '@/utils/heal';

const routes: RouteRecordRaw[] = [
  {
    path: '/login',
    name: 'login',
    component: () => import('@/views/LoginView.vue'),
    meta: { public: true },
  },
  { path: '/', name: 'village', component: () => import('@/views/VillageView.vue') },
  { path: '/map', name: 'map', component: () => import('@/views/MapView.vue') },
  { path: '/titles', name: 'titles', component: () => import('@/views/TitlesView.vue') },
  { path: '/military', name: 'military', component: () => import('@/views/MilitaryView.vue') },
  { path: '/market', name: 'market', component: () => import('@/views/MarketView.vue') },
  { path: '/shop', name: 'shop', component: () => import('@/views/ShopView.vue') },
  { path: '/tasks', name: 'quests', component: () => import('@/views/QuestsView.vue') },
  { path: '/reports', name: 'reports', component: () => import('@/views/ReportsView.vue') },
  { path: '/simulator', name: 'simulator', component: () => import('@/views/SimulatorView.vue') },
  { path: '/alliance', name: 'alliance', component: () => import('@/views/AllianceView.vue') },
  {
    path: '/leaderboard',
    name: 'leaderboard',
    component: () => import('@/views/LeaderboardView.vue'),
  },
  // Public. Readable signed out, so threads are linkable and the game is
  // visible to anyone still deciding whether to play.
  {
    path: '/forum',
    name: 'forum',
    component: () => import('@/views/ForumView.vue'),
    meta: { public: true },
  },
  // The manual, rendered from /documentation. Public for the same reason the
  // forum is: the rules are the argument for playing.
  {
    path: '/guide',
    name: 'guide',
    component: () => import('@/views/GuideView.vue'),
    meta: { public: true },
  },
  {
    path: '/guide/:chapter',
    name: 'guide-chapter',
    component: () => import('@/views/GuideView.vue'),
    meta: { public: true },
  },
  // The AI stewards' decision log. Signed-in only, like the game itself.
  {
    path: '/ai',
    name: 'ai-log',
    component: () => import('@/views/AiLogView.vue'),
  },
  // The admin panel. The page itself refuses non-admins; the API enforces it.
  {
    path: '/admin',
    name: 'admin',
    component: () => import('@/views/AdminView.vue'),
  },
  /*
   * Where the artwork came from.
   *
   * Public, and reachable without an account, because the CC BY-SA licences on
   * the heraldry ask for attribution wherever the work appears. A credits page
   * behind a login is not attribution.
   */
  {
    path: '/worlds',
    name: 'worlds',
    component: () => import('@/views/WorldsView.vue'),
  },
  {
    path: '/research',
    name: 'research',
    component: () => import('@/views/ResearchView.vue'),
  },
  {
    path: '/profile',
    name: 'profile',
    component: () => import('@/views/ProfileView.vue'),
  },
  {
    path: '/inbox',
    name: 'inbox',
    component: () => import('@/views/InboxView.vue'),
  },
  {
    path: '/credits',
    name: 'credits',
    component: () => import('@/views/CreditsView.vue'),
    meta: { public: true },
  },
  // A sketch, not a feature: the terrain renderer over a real coastline.
  {
    path: '/europe',
    name: 'europe',
    component: () => import('@/views/EuropeView.vue'),
    meta: { public: true },
  },
  {
    path: '/forum/search',
    name: 'forum-search',
    component: () => import('@/views/ForumSearchView.vue'),
    meta: { public: true },
  },
  {
    path: '/forum/board/:boardId',
    name: 'forum-board',
    component: () => import('@/views/ForumBoardView.vue'),
    meta: { public: true },
  },
  {
    path: '/forum/thread/:threadId',
    name: 'forum-thread',
    component: () => import('@/views/ForumThreadView.vue'),
    meta: { public: true },
  },
  { path: '/:pathMatch(.*)*', redirect: '/' },
];

export const router = createRouter({
  history: createWebHistory(),
  routes,
  scrollBehavior: () => ({ top: 0 }),
});

/**
 * Whether this copy of the app is serving as the forum's own site.
 *
 * forum.waldermarch.com is the same build behind the same nginx; the
 * hostname is the mode switch. On the forum host, forum pages (and the few
 * public pages a forum reasonably links: the manual, credits, sign-in) are
 * served here, and anything that is actually the game hands the visitor
 * back to the main domain with their path intact. The game host is
 * untouched: /forum still works there, which is also the fallback while
 * the subdomain's DNS record is still propagating.
 */
export const IS_FORUM_HOST =
  typeof location !== 'undefined' && location.hostname.startsWith('forum.');

const FORUM_HOST_ROUTES = new Set([
  'forum',
  'forum-board',
  'forum-thread',
  'forum-search',
  'guide',
  'credits',
  'login',
]);

/** The forum routes themselves, for the opposite hand-off below. */
const FORUM_ROUTES = new Set(['forum', 'forum-board', 'forum-thread', 'forum-search']);

/**
 * On the real domain the split is total: the game never renders the forum
 * and the forum never renders the game. In dev there is no subdomain, so
 * the in-game forum stays as the way to reach it at all.
 */
const ON_GAME_DOMAIN =
  typeof location !== 'undefined' &&
  location.hostname.endsWith('waldermarch.com') &&
  !location.hostname.startsWith('forum.');

router.beforeEach(async (to) => {
  const auth = useAuthStore();

  // Wait for the initial session check before deciding anything.
  if (auth.initialising) await auth.restore();

  if (IS_FORUM_HOST) {
    if (to.path === '/') return { name: 'forum' };
    if (typeof to.name === 'string' && !FORUM_HOST_ROUTES.has(to.name)) {
      window.location.href = `https://waldermarch.com${to.fullPath}`;
      return false;
    }
  }

  if (ON_GAME_DOMAIN && typeof to.name === 'string' && FORUM_ROUTES.has(to.name)) {
    window.location.href = `https://forum.waldermarch.com${to.fullPath}`;
    return false;
  }

  // Only the login page bounces a signed-in player away. Other public pages,
  // the forum, must stay reachable whether or not you are signed in.
  if (to.name === 'login') {
    return auth.isAuthenticated ? { name: 'village' } : true;
  }
  if (to.meta.public) return true;

  if (!auth.isAuthenticated) {
    return { name: 'login', query: to.fullPath === '/' ? {} : { redirect: to.fullPath } };
  }
  return true;
});

/*
 * A stale tab healing itself should land where the player was going.
 *
 * After a deploy, the first lazy view a stale tab opens fails to download
 * (its chunk was replaced). The window-level vite:preloadError handler
 * reloads, but a bare reload lands on the CURRENT route: the player clicked
 * Reports and got a refresh of the village instead, which reads as the
 * button being broken. The router knows where the click was headed, so the
 * reload goes there.
 */
router.onError((error, to) => {
  const text = String((error as Error)?.message ?? error);
  const chunkDied = /Failed to fetch dynamically imported|Importing a module script failed|error loading dynamically imported/i.test(text);
  if (chunkDied && to?.fullPath) {
    healStaleBuild(to.fullPath);
  }
});

export default router;
